import mediator from '../utilities/mediator';
import log from '../utilities/log';

import { ApiErrorResponse, Sensor } from '../models';
import {
  mutationOptions,
  queryOptions,
  UseMutationOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import { SensorNoteAddRequest } from 'adc-sirius-module-common';

type SensorsQueryParams = {
  studyKey?: string;
  siteKey?: string;
  subjectKey?: string;
  status?: string;
};

type SensorQueryParams = {
  key?: string;
};

type UpdateSensorVariables = {
  sensorKey: Sensor['key'];
  updatedSensorValues: Partial<Sensor>;
};

type CreateSensorNoteVariables = {
  sensorKey: string;
  noteAddRequest: SensorNoteAddRequest;
};

export function sensorsQueryOptions(
  params: SensorsQueryParams,
  options?: Partial<UseQueryOptions<Sensor[]>>,
) {
  return queryOptions<Sensor[]>({
    ...options,
    queryKey: ['sensors', params],
    queryFn: async () => {
      try {
        return await mediator.request<Sensor[]>('sensor:getSensors', {
          ...params,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}

export function sensorQueryOptions(
  params: SensorQueryParams,
  options?: Partial<UseQueryOptions<Sensor>>,
) {
  return queryOptions<Sensor>({
    ...options,
    queryKey: ['sensors', params],
    queryFn: async () => {
      try {
        return await mediator.request<Sensor>('sensor:getSensorByKey', {
          ...params,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!params?.key,
  });
}

export function updateSensorMutateOptions(
  options?: Partial<UseMutationOptions<Sensor, Error, UpdateSensorVariables>>,
) {
  return mutationOptions<Sensor, Error, UpdateSensorVariables>({
    ...options,
    mutationFn: async ({ updatedSensorValues }: UpdateSensorVariables) => {
      try {
        return await mediator.request<Sensor>('sensor:updateSensor', {
          data: updatedSensorValues,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}

export function createSensorNoteMutateOptions(
  options?: Partial<
    UseMutationOptions<Sensor, Error, CreateSensorNoteVariables>
  >,
) {
  return mutationOptions<Sensor, Error, CreateSensorNoteVariables>({
    ...options,
    mutationFn: async ({
      sensorKey,
      noteAddRequest,
    }: CreateSensorNoteVariables) => {
      try {
        return await mediator.request<Sensor>('sensor:createSensorNote', {
          key: sensorKey,
          data: noteAddRequest,
        });
      } catch (error: any) {
        log.error('Error creating sensor note', {
          status: error?.status ?? error?.response?.status,
          message: error?.message ?? error?.response?.data?.message,
        });
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}
